import React, { useEffect, useState } from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import Header from "../../layout/dashboard/header";
import HospitalContent from "../../components/hospital/HospitalContent";
import { useMutation } from "@tanstack/react-query";
import {
  apiGetFacility,
  apiGetGeneralFacilityAnalytics,
} from "../../api/facility";
import { useNotificationService } from "../../components/notification";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { FacilityStatsProps } from "../../interface/facility";

const Hospital: React.FC = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  const [facility_list, setFacility_list] = useState<any>([]);
  const [filter_data, setFilter_data] = useState("");
  const [stats, setStats] = useState<FacilityStatsProps>({
    activeDocument: 0,
    inactiveDocument: 0,
    totalFacility: 0,
  });

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First call stats
        await statsRequest.mutateAsync();
        // Then call jobs
        await mutation.mutateAsync({
          page: pagination_state.page,
          filter: filter_data,
        });
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state?.page, filter_data]);
  const mutation = useMutation({
    mutationFn: (field: { page: number; filter: string }) =>
      apiGetFacility(field),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setFacility_list(data.data.data);
        } else {
          setFacility_list([]);
        }
      }
    },
    onError: (error) => {
      console.error("Fetch process failed", error);
      showNotification("Fetch process failed", "error");
    },
  });

  const statsRequest = useMutation({
    mutationFn: () => apiGetGeneralFacilityAnalytics(),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        // Handle stats data here
        setStats(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });
  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: theme.palette.secondary.dark,
              }}
            >
              Hospital Management
            </Typography>
          </Stack>
        </Box>
      </Header>

      <HospitalContent
        setFilterData={setFilter_data}
        facilityList={facility_list}
        loading={mutation.isPending}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
        stats={stats}
      />
    </>
  );
};

export default Hospital;
